import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Trophy, Medal, Fire, Lightning, Crown, Star, 
  Rocket, ChartLineUp, Calendar, GameController 
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACHIEVEMENT_ICONS = {
  'gamepad': GameController,
  'trophy': Trophy,
  'fire': Fire,
  'crown': Crown,
  'medal': Medal,
  'star': Star,
  'lightning': Lightning,
  'calendar': Calendar,
  'chart': ChartLineUp,
  'rocket': Rocket,
  'arrow-up': Rocket,
  'trophy-gold': Trophy
};

export default function AchievementsPage() {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAchievements();
  }, [user?.id]);

  const fetchAchievements = async () => {
    if (!user?.id) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/users/${user.id}/stats`, { withCredentials: true });
      setAchievements(Array.isArray(data.achievements) ? data.achievements : []);
      setStats(data);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const unlockedCount = achievements.filter(a => a.unlocked).length;
  const totalCount = achievements.length;
  const progressPercent = totalCount > 0 ? (unlockedCount / totalCount) * 100 : 0;

  return (
    <div className="app-container" data-testid="achievements-page">
      {/* Header */}
      <header className="p-6 pb-4">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            <Medal size={24} weight="duotone" className="inline mr-2 text-[#FACC15]" />
            Achievements
          </h1>
          <p className="text-[#A1A1AA] text-sm">Collect badges and earn XP</p>
        </motion.div>
      </header>

      {/* Progress Overview */}
      <div className="px-6 mb-6">
        <div className="p-4 rounded-2xl bg-[#0F0F16] border border-white/5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[#A1A1AA] text-sm">Progress</span>
            <span className="text-white font-bold">{unlockedCount}/{totalCount}</span>
          </div>
          <div className="h-3 bg-[#181824] rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
              className="h-full bg-gradient-to-r from-[#9D4CDD] to-[#00F0FF] rounded-full"
            />
          </div>
          
          {/* XP Display */}
          {stats && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Star size={20} weight="fill" className="text-[#FACC15]" />
                <span className="text-white font-semibold">Level {stats.level}</span>
              </div>
              <div className="text-[#A1A1AA] text-sm">
                {stats.xp} / {stats.xp_for_next_level} XP
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Achievements Grid */}
      <div className="px-6 pb-32">
        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-32 rounded-2xl bg-[#0F0F16] animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3" data-testid="achievements-grid">
            {achievements.map((achievement, index) => {
              const IconComponent = ACHIEVEMENT_ICONS[achievement.icon] || Medal;
              return (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 rounded-2xl flex flex-col items-center text-center ${
                    achievement.unlocked
                      ? 'bg-gradient-to-br from-[#0F0F16] to-[#1a1a2e] border border-[#9D4CDD]/30'
                      : 'bg-[#0F0F16] border border-white/5 opacity-50'
                  }`}
                  data-testid={`achievement-${achievement.id}`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                    achievement.unlocked
                      ? 'bg-gradient-to-br from-[#9D4CDD]/20 to-[#00F0FF]/20'
                      : 'bg-[#181824]'
                  }`}>
                    <IconComponent 
                      size={24} 
                      weight={achievement.unlocked ? 'fill' : 'duotone'}
                      className={achievement.unlocked ? 'text-[#FACC15]' : 'text-[#71717A]'}
                    />
                  </div>
                  <h3 className={`text-sm font-semibold ${achievement.unlocked ? 'text-white' : 'text-[#71717A]'}`}>
                    {achievement.name}
                  </h3>
                  <p className="text-[#71717A] text-xs mt-1 line-clamp-2">{achievement.description}</p>
                  {achievement.unlocked && (
                    <p className="text-[#39FF14] text-[10px] mt-2">+15 XP</p>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
